import { useEffect, useState } from "react";
import { HeartHandshake, Loader2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import AssociationRecap from "@/components/admin/AssociationRecap";

const EMPTY = { name: "", code: "", areas: "", pickup_address: "", fee: "0", active: true, email: "", distribution_weekday: "", distribution_time: "" };
const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

function AssociationForm({ initial, editingId, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, fee: parseFloat(String(form.fee || 0).replace(",", ".")) || 0, distribution_weekday: form.distribution_weekday === "" ? null : Number(form.distribution_weekday) };
      if (editingId) await api.put(`/admin/associations/${editingId}`, payload);
      else await api.post("/admin/associations", payload);
      toast.success(editingId ? "Association mise à jour" : "Association créée");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="association-form" onSubmit={submit} className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-semibold text-bark">{editingId ? "Modifier l'association" : "Nouvelle association"}</h3>
        <button type="button" data-testid="association-form-close" onClick={onClose} className="text-stone-mute hover:text-bark" aria-label="Fermer"><X size={18} /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-bark-soft">Nom *
          <input data-testid="association-name-input" required value={form.name} onChange={set("name")} className={`${inputClass} mt-1`} placeholder="Les Abeilles du Vercors" />
        </label>
        <label className="text-sm text-bark-soft">Code unique *
          <input data-testid="association-code-input" required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, "") }))} className={`${inputClass} mt-1 font-mono tracking-wider`} placeholder="ABEILLES42" />
        </label>
        <label className="text-sm text-bark-soft sm:col-span-2">Codes postaux ou villes desservis *
          <textarea data-testid="association-areas-input" required rows={2} value={form.areas} onChange={set("areas")} className={`${inputClass} mt-1`} placeholder="38220, 38000, Grenoble, Vizille" />
          <span className="mt-1 block text-xs text-stone-mute">Séparez par des virgules. Un code partiel (ex. 38) dessert tout le département.</span>
        </label>
        <label className="text-sm text-bark-soft sm:col-span-2">Adresse / lieu de retrait
          <input data-testid="association-address-input" value={form.pickup_address} onChange={set("pickup_address")} className={`${inputClass} mt-1`} placeholder="Salle des fêtes, 2 place de la Mairie, 38220 Vizille" />
        </label>
        <label className="text-sm text-bark-soft sm:col-span-2">E-mail de l'association (réception du récapitulatif)
          <input data-testid="association-email-input" type="email" value={form.email} onChange={set("email")} className={`${inputClass} mt-1`} placeholder="contact@association.fr" />
        </label>
        <label className="text-sm text-bark-soft">Jour de distribution (chaque semaine)
          <select data-testid="association-weekday-select" value={form.distribution_weekday} onChange={set("distribution_weekday")} className={`${inputClass} mt-1`}>
            <option value="">— Aucun —</option>
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </label>
        <label className="text-sm text-bark-soft">Heure de distribution
          <input data-testid="association-time-input" type="time" value={form.distribution_time} onChange={set("distribution_time")} className={`${inputClass} mt-1`} />
        </label>
        <label className="text-sm text-bark-soft">Frais de livraison (€)
          <input data-testid="association-fee-input" inputMode="decimal" value={form.fee} onChange={set("fee")} className={`${inputClass} mt-1`} />
        </label>
        <label className="flex items-center gap-2 self-end pb-3 text-sm text-bark-soft">
          <input data-testid="association-active-checkbox" type="checkbox" checked={form.active} onChange={set("active")} className="h-4 w-4 accent-amber-deep" /> Active
        </label>
      </div>
      <button data-testid="association-save-button" disabled={saving} className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey disabled:opacity-60">
        {saving && <Loader2 size={15} className="animate-spin" />} Enregistrer
      </button>
    </form>
  );
}

function AssociationRow({ a, onEdit, onToggle, onChanged }) {
  return (
    <div data-testid={`association-row-${a.code}`} className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-800/15 bg-white p-5 ${a.active ? "" : "opacity-60"}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-bark">
          {a.name} <span className="ml-2 rounded-full bg-amber-50 px-2.5 py-0.5 font-mono text-xs text-amber-deep">{a.code}</span>
          {!a.active && <span className="ml-2 rounded-full bg-sand px-2.5 py-0.5 text-xs text-bark-soft">Désactivée</span>}
        </p>
        <p className="mt-1 text-xs text-stone-mute">Dessert : {a.areas.join(", ")}</p>
        {a.pickup_address && <p className="text-xs text-stone-mute">Retrait : {a.pickup_address}</p>}
        {a.next_distribution_label && <p data-testid={`association-next-${a.code}`} className="text-xs text-stone-mute">Prochaine distribution : {a.next_distribution_label}</p>}
        {a.email && <p className="text-xs text-stone-mute">E-mail : {a.email} · récapitulatif envoyé automatiquement la veille</p>}
        <p className="text-xs text-stone-mute">{a.fee_cents ? formatPrice(a.fee_cents) : "Gratuit"} · {a.orders_count || 0} commande(s) payée(s)</p>
        <div className="mt-2"><AssociationRecap association={a} onChanged={onChanged} /></div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button data-testid={`toggle-association-${a.code}`} onClick={() => onToggle(a)} className="rounded-full border border-amber-800/20 px-4 py-1.5 text-xs font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep">
          {a.active ? "Désactiver" : "Réactiver"}
        </button>
        <button data-testid={`edit-association-${a.code}`} onClick={() => onEdit(a)} className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep" aria-label={`Modifier ${a.name}`}>
          <Pencil size={15} />
        </button>
      </div>
    </div>
  );
}

export default function AdminAssociations() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => api.get("/admin/associations").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    try {
      await api.post(`/admin/associations/${a.association_id}/active`, { active: !a.active });
      toast.success(a.active ? "Association désactivée" : "Association réactivée");
      load();
    } catch {
      toast.error("Action impossible");
    }
  };

  const edit = (a) => setForm({
    id: a.association_id,
    initial: { name: a.name, code: a.code, areas: a.areas.join(", "), pickup_address: a.pickup_address || "", fee: (a.fee_cents / 100).toFixed(2), active: a.active, email: a.email || "", distribution_weekday: a.distribution_weekday ?? "", distribution_time: a.distribution_time || "" },
  });

  return (
    <section data-testid="admin-associations" className="space-y-4 border-t border-amber-800/15 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-bark"><HeartHandshake size={22} className="text-amber-deep" /> Associations partenaires</h2>
          <p className="mt-1 text-sm text-bark-soft">Le client saisit son code postal et le code de son association : seule celle-ci lui est proposée.</p>
        </div>
        <button data-testid="new-association-button" onClick={() => setForm({ id: null, initial: EMPTY })} className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95">
          <Plus size={16} /> Nouvelle association
        </button>
      </div>
      {form && <AssociationForm key={form.id || "new"} initial={form.initial} editingId={form.id} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {items === null ? (
        <Loader2 className="animate-spin text-amber-deep" />
      ) : items.length === 0 ? (
        <p data-testid="associations-empty" className="rounded-2xl border border-dashed border-amber-800/20 p-6 text-sm text-bark-soft">Aucune association pour le moment.</p>
      ) : (
        <div className="space-y-3">{items.map((a) => <AssociationRow key={a.association_id} a={a} onEdit={edit} onToggle={toggle} onChanged={load} />)}</div>
      )}
    </section>
  );
}
